import Hls from 'hls.js';
import { getConfig } from '../app/config.js';
import { log_error_js } from '../app/logging.js';
import {currentTimestamp} from "../helpers/time.js";

export default function MediaPlayer(props = {}, store) {

    let $button_mute;
    let $button_mute_icon;
    let $button_play;
    let $button_play_icon;
    let $hidden_container;
    let $track_live;
    let $track_loading;
    let $track_name;
    let $track_time;
    let $player;
    let $video;
    let $volume_slider;


    let hls;
    let retryTimer = null;
    let retryStartTime = null;
    let reconnecting = false;

    // Playlist state
    let playlist = [];
    let playlistLoop = true;
    let currentTrackIndex = -1;
    let liveStream = false;
    let currentService = null;


    function getHlsConfig(service) {
        if (service === 'ketv') {
            return {
                capLevelToPlayerSize: true,
                autoStartLoad: true,
                enableWorker: true,
                lowLatencyMode: true,
                liveSyncDurationCount: 3,
                liveMaxLatencyDurationCount: 10,
                liveBackBufferLength: 30
            };
        }

        if (service === 'vod') {
            const auth = store.get("auth");
            const token = auth ? auth.token : null;

            return {
                capLevelToPlayerSize: true,
                autoStartLoad: true,
                enableWorker: true,
                lowLatencyMode: false,
                maxBufferLength: 30,
                maxMaxBufferLength: 60,
                xhrSetup: function(xhr) {
                    if (token) {
                        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                    }
                }
            };
        }
    }

    function updateUI() {
        const playing = store.get("playing");
        const metadata = store.get("currentTrack");

        $track_name.textContent = metadata ? `${metadata.title}` : "Unknown Track";

        if (liveStream) {
            $track_live.classList.remove('hidden');
        } else {
            $track_live.classList.add('hidden');
        }

        if (playing && reconnecting) {
            $track_loading.classList.remove('hidden');
        } else {
            $track_loading.classList.add('hidden');
        }

        $button_play.title = playing ? "Pause" : "Play";
        $button_play_icon.classList.toggle('fa-pause', playing);
        $button_play_icon.classList.toggle('fa-play', !playing);
    }

    function setupMediaSession(metadata) {
        if ('mediaSession' in navigator && metadata) {
            const artwork = metadata.artwork
                ? [{ src: metadata.artwork, sizes: '512x512', type: 'image/png' }]
                : [{ src: 'public/assets/logos/logo.png', sizes: '512x512', type: 'image/png' }];

            navigator.mediaSession.metadata = new MediaMetadata({
                title: metadata.title,
                artist: metadata.artist,
                artwork: artwork
            });

            if ('setPositionState' in navigator.mediaSession) {
                if (!liveStream && metadata.duration && !isNaN(metadata.duration)) {
                    navigator.mediaSession.setPositionState({
                        duration: metadata.duration,
                        playbackRate: 1,
                        position: $video ? $video.currentTime : 0
                    });
                } else {
                    navigator.mediaSession.setPositionState(null);
                }
            }

            navigator.mediaSession.setActionHandler('play', () => {
                if ($video) $video.play();
                store.set("playing", true);
            });

            navigator.mediaSession.setActionHandler('pause', () => {
                if ($video) $video.pause();
                store.set("playing", false);
            });
        }
    }

    function handleRetry() {
        const playing = store.get("playing");

        if (retryTimer) {
            if (playing && !reconnecting) {
                reconnecting = true;
                updateUI();
            }
            return;
        }

        if (playing) {
            reconnecting = true;
            $track_loading.classList.remove('hidden');
        }
        updateUI();

        if ($video && !$video.paused) {
            $video.pause();
        }

        if (!retryStartTime) retryStartTime = Date.now();
        const elapsed = (Date.now() - retryStartTime) / 1000;
        let interval = 10000; // Default 10s

        if (elapsed < 300) {
            // Under 5 minutes: 10s
            interval = 10000;
        } else {
            // Over 5 minutes: 30s forever
            interval = 30000;
        }

        console.log(`Stream disconnected. Retrying in ${interval / 1000}s... (Elapsed: ${Math.round(elapsed)}s)`);

        retryTimer = setTimeout(() => {
            retryTimer = null;
            if (hls) {
                console.log("Attempting to reconnect to stream...");
                const current_stream_url = playlist[currentTrackIndex].url;
                hls.loadSource(current_stream_url);
                hls.startLoad();
            }
        }, interval);
    }

    function play () {
        if (!store.get("playing")) store.set("playing", true);
        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = "playing";
        }
        setupMediaSession(store.get("currentTrack"));
    }

    function pause () {
        if (store.get("playing")) store.set("playing", false);
        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = "paused";
        }
        setupMediaSession(store.get("currentTrack"));
    }

    function togglePlay () {
        if ($video && $video.paused) {
            store.set("playing", true);
            $video.play().catch(e => {
                console.warn("Play failed:", e);

                if (retryTimer || !hls || hls.streamError) {
                    handleRetry();
                }
            });
        } else if ($video) {
            $video.pause();
            store.set("playing", false);
        }
    }

    function toggleMute () {
        $video.muted = !$video.muted;
        updateVolumeIcon();
    }

    function updateVolumeIcon() {
        if ($video.muted || $video.volume === 0) {
            $button_mute_icon.className = 'fas fa-volume-off';
        } else if ($video.volume < 0.33) {
            $button_mute_icon.className = 'fas fa-volume-low';
        } else if ($video.volume < 0.66) {
            $button_mute_icon.className = 'fas fa-volume';
        } else {
            $button_mute_icon.className = 'fas fa-volume-high';
        }
    }

    function adjustVolumeVideo () {
        if ($volume_slider) $volume_slider.value = $video.volume;

        updateVolumeIcon();
    }

    function adjustVolumeSlider () {
        $video.volume = $volume_slider.value;
        $video.muted = ($video.volume === 0);

        updateVolumeIcon();
    }

    function updateTrackTime() {
        let seconds = Math.floor($video.currentTime);
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        seconds = seconds % 60;

        let timeString = hours > 0
            ? `${hours}:${mins.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            : `${mins.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        // For on-demand tracks, show current time / duration
        if (!liveStream && currentTrackIndex >= 0) {
            const track = playlist[currentTrackIndex];
            if (track && track.duration && !isNaN(track.duration)) {
                let totalSec = Math.floor(track.duration);
                const totalHours = Math.floor(totalSec / 3600);
                const totalMins = Math.floor((totalSec % 3600) / 60);
                totalSec = totalSec % 60;

                const durationString = totalHours > 0
                    ? `${totalHours}:${totalMins.toString().padStart(2, '0')}:${totalSec.toString().padStart(2, '0')}`
                    : `${totalMins.toString().padStart(2, '0')}:${totalSec.toString().padStart(2, '0')}`;

                timeString += ` / ${durationString}`;
            }
        }

        $track_time.textContent = timeString;
    }

    function loadTrack(index) {
        if (playlist.length === 0) {
            console.warn('Playlist is empty.');
            return;
        }

        if (!playlistLoop && (index < 0 || index >= playlist.length)) {
            console.warn('Invalid track index:', index);
            return;
        }

        if (index < 0) {
            index = playlist.length - 1;
        } else if (index >= playlist.length) {
            index = 0;
        }

        // Clear retry state when switching tracks
        if (retryTimer) {
            clearTimeout(retryTimer);
            retryTimer = null;
        }
        retryStartTime = null;
        reconnecting = false;

        const track = playlist[index];
        currentTrackIndex = index;

        liveStream = track.live ?? false;
        currentService = track.service;

        const meta = {
            title: track.title || 'Unknown Track',
            artist: track.artist || 'King Earthquake',
            duration: track.duration,
            artwork: track.artwork,
            live: track.live ?? false,
        };
        store.set("currentTrack", meta);
        setupMediaSession(meta);

        if (Hls.isSupported()) {
            if (hls) {
                hls.destroy();
                hls = null;
            }

            hls = new Hls(getHlsConfig(currentService));

            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    if (!liveStream) {
                        console.error('Fatal HLS error on track playback:', data);
                        log_error_js({
                            type: 'hls_error',
                            message: `Fatal HLS error on track: ${data.type} - ${data.details}`,
                            url: window.location.href,
                            timestamp: currentTimestamp(),
                        });
                        return;
                    }

                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            console.error("Network error encountered:", data.details);
                            // For fatal network errors, we always try to recover.
                            // If it's a manifest error, we use our tiered retry logic.
                            if (data.details === 'manifestLoadError' || data.details === 'manifestParsingError' || data.details === 'levelLoadError' || data.details === 'fragLoadError') {
                                handleRetry();
                            } else {
                                console.log("Fatal network error (not manifest/level/frag), trying hls.startLoad()");
                                hls.startLoad();
                            }
                            break;

                        case Hls.ErrorTypes.MEDIA_ERROR:
                            console.error("Media error encountered, trying to recover...");
                            log_error_js({
                                type: 'hls_error',
                                message: `Fatal HLS media error: ${data.details}`,
                                url: window.location.href,
                                timestamp: currentTimestamp(),
                            });

                            if (data.details === 'bufferStalledError') {
                                handleRetry();
                            } else {
                                hls.recoverMediaError();
                            }
                            break;

                        default:
                            console.error("Fatal error, triggering retry logic:", data.details);
                            log_error_js({
                                type: 'hls_error',
                                message: `Fatal HLS error: ${data.type} - ${data.details}`,
                                url: window.location.href,
                                timestamp: currentTimestamp(),
                            });
                            handleRetry();
                            break;
                    }

                } else if (liveStream && data.type === Hls.ErrorTypes.MEDIA_ERROR && data.details === 'bufferStalledError') {
                    const latency = hls.liveSyncPosition - $video.currentTime;
                    // Try to jump to live edge if we stall
                    if (latency > 20) {
                        console.warn("Buffer stalled, jumping to live edge");
                        if ($video) $video.currentTime = hls.liveSyncPosition;
                    }
                }
            });

            hls.loadSource(track.url);
            hls.attachMedia($video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                reconnecting = false;
                if (retryTimer) {
                    clearTimeout(retryTimer);
                    retryTimer = null;
                }
                retryStartTime = null;

                updateUI();

                // Autoplay if store says playing
                if (store.get("playing")) {
                    if ($video) $video.play().catch(e => console.warn("Autoplay blocked:", e));
                }
            });
        } else {
            alert('Video format not supported.');
        }
    }

    function handleTrackEnded() {
        if (!liveStream && playlist.length > 0) {
            loadTrack(currentTrackIndex + 1);
        }
    }

    function clearPlaylist() {
        if (retryTimer) clearTimeout(retryTimer);
        if (hls) {
            hls.destroy();
            hls = null;
        }

        playlist = [];
        currentTrackIndex = -1;
        liveStream = false;
        currentService = null;
        store.set("playing", false);
        store.set("currentTrack", null)
    }

    return {
        teleport(targetId) {
            const target = document.getElementById(targetId);
            if (target && $video) {
                if ($video.parentElement === target) return;

                target.innerHTML = '';
                target.appendChild($video);
                $video.style.display = 'block';
                $video.controls = true;

                if (hls) {
                    if (hls.config.capLevelToPlayerSize !== true) {
                        hls.config.capLevelToPlayerSize = true;
                    }
                    if (hls.currentLevel !== -1) {
                        hls.currentLevel = -1;
                    }
                }

                if (store.get("playing")) {
                    if ($video.paused) {
                        $video.play().catch(e => {
                            console.warn("Auto-play blocked after teleport:", e);
                            if ($video.paused) store.set("playing", false);
                        });
                    }
                }
            }
        },

        resetLocation() {
            if ($hidden_container && $video) {
                // If it's already in the hidden container, do nothing
                if ($video.parentElement === $hidden_container) return;

                $hidden_container.appendChild($video);
                $video.style.display = 'none'; // Back to display none for video

                if (hls) {
                    hls.currentLevel = 0;
                    if (hls.config.capLevelToPlayerSize !== false) {
                        hls.config.capLevelToPlayerSize = false;
                    }
                }

                // If it was playing, ensure it keeps playing in the background
                if (store.get("playing")) {
                    if ($video.paused) {
                        $video.play().catch(e => console.warn("Background play failed:", e));
                    }
                }
            }
        },

        render() {
            $player = document.createElement('div');
            $player.id = 'persistent-media-player';
            $player.classList.add('hidden');
            $player.innerHTML = `
                <div class="hidden-video-storage" style="display: none;"></div>
                <div class="info">
                    <button class="play-btn" title="Play">
                        <i class="fas fa-play"></i>
                    </button>
                    <span class="track-live hidden">LIVE</span>
                    <span class="track-name"></span>
                    <i class="track-loading fas fa-circle-notch fa-spin hidden" aria-label="Loading"></i>
                    <div class="spacer"></div>
                    <span class="track-time time">00:00</span>
                    <div class="volume-control desktop">
                        <button class="mute-btn" style="background: none; border: none; color: #fff; cursor: pointer;" aria-label="Mute">
                            <i class="fas fa-volume-up"></i>
                        </button>
                        <input type="range" id="MediaPlayerVolume" min="0" max="1" step="0.1" value="1" style="width: 80px; cursor: pointer;" aria-label="Volume Control">
                    </div>
                </div>
            `;
            return $player;
        },

        loadPlaylist(tracks, position = 0) {
            if (!Array.isArray(tracks) || tracks.length === 0) {
                console.warn('Invalid playlist provided');
                return;
            }

            playlist = tracks;

            loadTrack(position);

            $player.classList.remove('hidden');
        },

        play(index = 0) {
            loadTrack(index);
        },

        next() {
            if (!liveStream && currentTrackIndex < playlist.length - 1) {
                loadTrack(currentTrackIndex + 1);
            }
        },

        previous() {
            if (!liveStream && currentTrackIndex > 0) {
                loadTrack(currentTrackIndex - 1);
            }
        },

        getCurrentTrack() {
            if (currentTrackIndex >= 0) {
                return {
                    track: playlist[currentTrackIndex],
                    index: currentTrackIndex,
                    playlist_length: playlist.length
                };
            }
            return null;
        },

        service() {
            return currentService;
        },

        update_ketv_stream(online) {
            if (currentService === 'vod') {
                return;
            }

            if (online === true) {
                playlist = [
                    {
                        title: 'KETV Radio',
                        artist: 'King Earthquake',
                        url: getConfig('ketv_url'),
                        live: true,
                        service: 'ketv',
                    }
                ];

                $player.classList.remove('hidden');

                store.set("playing", false);

                loadTrack(0);
            } else {
                $player.classList.add('hidden');

                clearPlaylist();
            }
        },

        clear: clearPlaylist,

        _playing_unsubscribe: null,
        _playing_meta_unsubscribe: null,
        _ketv_status_unsubscribe: null,

        onMount() {
            if ($video) {
                console.log("Video already mounted");
                updateUI();
                return;
            }

            $hidden_container = $player.querySelector('.hidden-video-storage');

            $button_mute = $player.querySelector('.mute-btn');
            $button_mute_icon = $player.querySelector('.mute-btn i');
            $button_play = $player.querySelector('.play-btn');
            $button_play_icon = $player.querySelector('.play-btn i');
            $track_live = $player.querySelector('.track-live');
            $track_loading = $player.querySelector('.track-loading');
            $track_name = $player.querySelector('.track-name');
            $track_time = $player.querySelector('.track-time');
            $volume_slider = $player.querySelector('#MediaPlayerVolume');

            $video = document.createElement('video');
            $video.id = 'main-hls-video';
            $video.playsInline = true;
            $video.style.width = '100%';
            $video.style.aspectRatio = '16/9';
            $video.style.background = '#000';
            $video.style.display = 'none';
            $hidden_container.appendChild($video);

            $video.controlsList = "noplaybackrate nodownload"
            $video.disablePictureInPicture = true;
            $video.loop = false;

            $video.addEventListener('pause', pause);
            $video.addEventListener('play', play);
            $video.addEventListener('timeupdate', updateTrackTime);
            $video.addEventListener('volumechange', adjustVolumeVideo);
            $video.addEventListener('ended', handleTrackEnded);

            $button_mute.addEventListener('click', toggleMute);
            $button_play.addEventListener('click', togglePlay);
            $volume_slider.addEventListener('input', adjustVolumeSlider);

            this._playing_unsubscribe = store.subscribe("playing", updateUI);
            this._playing_meta_unsubscribe = store.subscribe("currentTrack", updateUI);

            this.update_ketv_stream(store.get('ketv').online);
            this._ketv_status_unsubscribe = store.subscribe('ketv', (ketvState) => {
                this.update_ketv_stream(ketvState.online)
            });

            updateUI();
        },

        onUnmount() {
            if (retryTimer) clearTimeout(retryTimer);

            if (hls) {
                hls.destroy();
                hls = null;
            }

            if ($video) {
                $video.removeEventListener('pause', pause);
                $video.removeEventListener('play', play);
                $video.removeEventListener('timeupdate', updateTrackTime);
                $video.removeEventListener('volumechange', adjustVolumeVideo);
                $video.removeEventListener('ended', handleTrackEnded);
            }

            if ($button_mute) $button_mute.removeEventListener('click', toggleMute);
            if ($button_play) $button_play.removeEventListener('click', togglePlay);
            if ($volume_slider) $volume_slider.removeEventListener('input', adjustVolumeSlider);


            if (this._playing_unsubscribe) this._playing_unsubscribe();
            if (this._playing_meta_unsubscribe) this._playing_meta_unsubscribe();
            if (this._ketv_status_unsubscribe) this._ketv_status_unsubscribe();

            if ('mediaSession' in navigator) {
                navigator.mediaSession.setActionHandler('play', null);
                navigator.mediaSession.setActionHandler('pause', null);
            }
        }
    };
}
